import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Activity,
  RefreshCcw,
  Filter,
  AlertCircle,
  Loader2,
  Users as UsersIcon,
  CheckCircle2,
  Clock,
  PlayCircle,
  X,
} from 'lucide-react';
import { agendamentoAPI, type OrdemServico } from '../../services/api';

/**
 * Agendamento > Monitoramento — dashboard de OS agrupado por técnico.
 *
 * Porte do legado Admin/Monitoramento.js (830 lns) — mantém a lógica
 * (3 status em paralelo, agrupamento por responsável, filtros multi-select)
 * mas adapta pro tema dark slate e backend proxy server-side (Fase 16d).
 *
 * Backend (services/api/src/modules/agendamento/routes/monitoramento.ts)
 * faz a chamada ao SGP usando ERP_URL/TOKEN/APP do Config (não mais
 * token hardcoded no JS como no legado).
 */

const STATUS_OPTIONS = [
  { value: 0, label: 'Aberta', sgpLabel: 'Aberta' },
  { value: 2, label: 'Em execução', sgpLabel: 'Em execução' },
  { value: 1, label: 'Encerrada', sgpLabel: 'Encerrada' },
] as const;

interface TecnicoOrdens {
  emExecucao: OrdemServico[];
  abertas: OrdemServico[];
  encerradas: OrdemServico[];
}

interface Filtros {
  tecnicos: string[]; // vazio = todos
  status: number[]; // 0/1/2
  motivos: string[]; // vazio = todos
  dataInicio: string; // YYYY-MM-DD
  dataFim: string;
}

const FILTROS_STORAGE_KEY = 'farisx_monitoramento_filtros';

function defaultDataInicio(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultDataFim(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function loadFiltros(): Filtros {
  try {
    const saved = localStorage.getItem(FILTROS_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<Filtros>;
      return {
        tecnicos: parsed.tecnicos ?? [],
        status: parsed.status ?? [0, 1, 2],
        motivos: parsed.motivos ?? [],
        dataInicio: parsed.dataInicio || defaultDataInicio(),
        dataFim: parsed.dataFim || defaultDataFim(),
      };
    }
  } catch {
    // fallback
  }
  return {
    tecnicos: [],
    status: [0, 1, 2],
    motivos: [],
    dataInicio: defaultDataInicio(),
    dataFim: defaultDataFim(),
  };
}

function formatarData(data?: string, hora?: string): string {
  if (!data) return 'Não agendado';
  const ddmmyyyy = data.split('-').reverse().join('/');
  return hora ? `${ddmmyyyy} ${hora}` : ddmmyyyy;
}

export default function AgendamentoMonitoramento() {
  const [ordens, setOrdens] = useState<OrdemServico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<Filtros>(loadFiltros);
  const [showFiltros, setShowFiltros] = useState(false);

  // Persist filtros
  useEffect(() => {
    localStorage.setItem(FILTROS_STORAGE_KEY, JSON.stringify(filtros));
  }, [filtros]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await agendamentoAPI.monitoramento.ordensServico({
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
      });
      setOrdens(r.ordens);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao carregar OS.');
      setOrdens([]);
    } finally {
      setLoading(false);
    }
  }, [filtros.dataInicio, filtros.dataFim]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-refresh 5min
  useEffect(() => {
    const interval = setInterval(() => void load(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  // Extrai listas únicas pra dropdowns
  const todosTecnicos = useMemo(() => {
    const set = new Set<string>();
    ordens.forEach((o) => {
      if (o.responsavel) set.add(o.responsavel);
    });
    return [...set].sort();
  }, [ordens]);

  const todosMotivos = useMemo(() => {
    const set = new Set<string>();
    ordens.forEach((o) => {
      if (o.motivo) set.add(o.motivo);
    });
    return [...set].sort();
  }, [ordens]);

  // Aplica filtros + agrupa por técnico
  const tecnicosAgrupados = useMemo(() => {
    let filtered = ordens;

    if (filtros.tecnicos.length > 0) {
      filtered = filtered.filter(
        (o) => o.responsavel && filtros.tecnicos.includes(o.responsavel),
      );
    }
    if (filtros.status.length > 0) {
      const statusLabels: string[] = STATUS_OPTIONS.filter((s) =>
        filtros.status.includes(s.value),
      ).map((s) => s.sgpLabel);
      filtered = filtered.filter((o) => o.status && statusLabels.includes(o.status));
    }
    if (filtros.motivos.length > 0) {
      filtered = filtered.filter((o) => o.motivo && filtros.motivos.includes(o.motivo));
    }

    const agrupados: Record<string, TecnicoOrdens> = {};
    filtered.forEach((o) => {
      if (!o.responsavel) return;
      if (!agrupados[o.responsavel]) {
        agrupados[o.responsavel] = { emExecucao: [], abertas: [], encerradas: [] };
      }
      const slot = agrupados[o.responsavel]!;
      if (o.status === 'Em execução') slot.emExecucao.push(o);
      else if (o.status === 'Aberta') slot.abertas.push(o);
      else if (o.status === 'Encerrada') slot.encerradas.push(o);
    });

    return agrupados;
  }, [ordens, filtros]);

  // Dashboard summary
  const dashboard = useMemo(() => {
    const items = Object.entries(tecnicosAgrupados).map(([tecnico, ords]) => ({
      tecnico,
      pendentes: ords.emExecucao.length + ords.abertas.length,
      encerradas: ords.encerradas.length,
    }));
    items.sort((a, b) => b.pendentes - a.pendentes || b.encerradas - a.encerradas);
    return items;
  }, [tecnicosAgrupados]);

  const totalPendentes = dashboard.reduce((s, i) => s + i.pendentes, 0);
  const totalEncerradas = dashboard.reduce((s, i) => s + i.encerradas, 0);

  const filtrosAtivos =
    filtros.tecnicos.length > 0 || filtros.status.length < 3 || filtros.motivos.length > 0;

  function clearAllFiltros(): void {
    setFiltros({
      tecnicos: [],
      status: [0, 1, 2],
      motivos: [],
      dataInicio: defaultDataInicio(),
      dataFim: defaultDataFim(),
    });
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <header className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary-soft/40 text-primary flex items-center justify-center">
            <Activity size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-fg">Monitoramento</h1>
            <p className="text-sm text-fg-2">
              {Object.keys(tecnicosAgrupados).length} técnico(s) ·{' '}
              <span className="text-warning">{totalPendentes} pendentes</span> ·{' '}
              <span className="text-success">{totalEncerradas} encerradas</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFiltros((v) => !v)}
            className={`relative flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition ${
              showFiltros
                ? 'bg-primary-soft/30 border-primary-line/50 text-primary'
                : 'bg-canvas hover:bg-hover border-line text-fg'
            }`}
          >
            <Filter size={14} />
            Filtros
            {filtrosAtivos && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary" />
            )}
          </button>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-active rounded-lg text-sm font-medium text-white transition"
          >
            <RefreshCcw size={14} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </header>

      {showFiltros && (
        <FiltrosPanel
          filtros={filtros}
          setFiltros={setFiltros}
          todosTecnicos={todosTecnicos}
          todosMotivos={todosMotivos}
          onClear={clearAllFiltros}
        />
      )}

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-danger-line/50 bg-danger-soft/20 px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center">
          <Loader2 size={28} className="animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted mt-3">Carregando ordens de serviço…</p>
        </div>
      ) : (
        <>
          {/* Dashboard resumo */}
          {dashboard.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xs font-bold uppercase tracking-wider text-primary mb-3 px-1">
                Resumo por técnico
              </h2>
              <div className="overflow-x-auto rounded-2xl border border-line bg-surface">
                <table className="w-full text-sm">
                  <thead className="bg-canvas border-b border-line">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted">
                      <th className="px-4 py-3">Técnico</th>
                      <th className="px-4 py-3 text-right">Pendentes</th>
                      <th className="px-4 py-3 text-right">Encerradas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {dashboard.map((d) => (
                      <tr key={d.tecnico} className="hover:bg-canvas/50">
                        <td className="px-4 py-2.5 text-fg font-medium">{d.tecnico}</td>
                        <td
                          className={`px-4 py-2.5 text-right font-bold ${
                            d.pendentes > 0 ? 'text-warning' : 'text-muted'
                          }`}
                        >
                          {d.pendentes}
                        </td>
                        <td
                          className={`px-4 py-2.5 text-right font-bold ${
                            d.pendentes === 0 && d.encerradas > 0
                              ? 'text-success'
                              : 'text-fg-2'
                          }`}
                        >
                          {d.encerradas}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-canvas border-t border-line">
                    <tr className="font-bold">
                      <td className="px-4 py-3 text-fg-2">Total</td>
                      <td className="px-4 py-3 text-right text-warning">{totalPendentes}</td>
                      <td className="px-4 py-3 text-right text-success">{totalEncerradas}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          )}

          {/* Cards por técnico */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Object.entries(tecnicosAgrupados)
              .sort(
                ([, a], [, b]) =>
                  b.emExecucao.length + b.abertas.length - a.emExecucao.length - a.abertas.length,
              )
              .map(([tecnico, ords]) => (
                <TecnicoCard
                  key={tecnico}
                  tecnico={tecnico}
                  ordens={ords}
                  statusFiltro={filtros.status}
                />
              ))}
          </div>

          {Object.keys(tecnicosAgrupados).length === 0 && (
            <div className="bg-surface border border-line rounded-2xl p-10 text-center">
              <UsersIcon size={32} className="text-faint mx-auto mb-3" />
              <p className="text-fg-2 font-semibold mb-1">
                Nenhuma OS encontrada
              </p>
              <p className="text-sm text-muted">
                Ajuste os filtros ou intervalo de datas pra ver mais resultados.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// Card por técnico
// ============================================================

function TecnicoCard({
  tecnico,
  ordens,
  statusFiltro,
}: {
  tecnico: string;
  ordens: TecnicoOrdens;
  statusFiltro: number[];
}) {
  const showEmExecucao = statusFiltro.includes(2);
  const showAbertas = statusFiltro.includes(0);
  const showEncerradas = statusFiltro.includes(1);

  return (
    <div className="bg-surface border border-line rounded-2xl p-4">
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-line">
        <div className="w-10 h-10 rounded-full bg-primary-solid/30 text-primary flex items-center justify-center font-bold">
          {tecnico.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-fg truncate">{tecnico}</h3>
          <div className="text-xs text-fg-2 flex gap-3 mt-0.5">
            {showEmExecucao && ordens.emExecucao.length > 0 && (
              <span className="flex items-center gap-1 text-primary">
                <PlayCircle size={11} /> {ordens.emExecucao.length}
              </span>
            )}
            {showAbertas && ordens.abertas.length > 0 && (
              <span className="flex items-center gap-1 text-warning">
                <Clock size={11} /> {ordens.abertas.length}
              </span>
            )}
            {showEncerradas && ordens.encerradas.length > 0 && (
              <span className="flex items-center gap-1 text-success">
                <CheckCircle2 size={11} /> {ordens.encerradas.length}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {showEmExecucao && (
          <OrdemList
            title="Em execução"
            color="blue"
            ordens={ordens.emExecucao}
            emptyText="Nenhuma OS em execução"
          />
        )}
        {showAbertas && (
          <OrdemList
            title="Abertas"
            color="yellow"
            ordens={ordens.abertas}
            emptyText="Nenhuma OS aberta"
          />
        )}
        {showEncerradas && (
          <OrdemList
            title="Encerradas"
            color="emerald"
            ordens={ordens.encerradas}
            emptyText="Nenhuma OS encerrada"
          />
        )}
      </div>
    </div>
  );
}

function OrdemList({
  title,
  color,
  ordens,
  emptyText,
}: {
  title: string;
  color: 'blue' | 'yellow' | 'emerald';
  ordens: OrdemServico[];
  emptyText: string;
}) {
  const colorClasses = {
    blue: 'border-primary-line/50 bg-primary-soft/10',
    yellow: 'border-warning-line/50 bg-warning-soft/10',
    emerald: 'border-success-line/50 bg-success-soft/10',
  }[color];
  const headerColor = {
    blue: 'text-primary',
    yellow: 'text-warning',
    emerald: 'text-success',
  }[color];

  if (ordens.length === 0) {
    return (
      <div>
        <h4 className={`text-xs font-bold uppercase mb-1.5 ${headerColor}`}>
          {title} (0)
        </h4>
        <p className="text-xs text-faint italic pl-2">{emptyText}</p>
      </div>
    );
  }

  return (
    <div>
      <h4 className={`text-xs font-bold uppercase mb-1.5 ${headerColor}`}>
        {title} ({ordens.length})
      </h4>
      <ul className="space-y-1.5">
        {ordens.map((o) => (
          <li
            key={o.id}
            className={`rounded-lg border ${colorClasses} p-2.5 text-xs`}
          >
            <div className="flex items-baseline gap-2 mb-0.5">
              <span className="font-mono font-bold text-fg">#{o.id}</span>
              <span className="text-fg-2 truncate flex-1">{o.cliente ?? '—'}</span>
            </div>
            {o.motivo && <div className="text-muted mb-0.5">{o.motivo}</div>}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-muted">
              {o.contrato && <span>Contrato {o.contrato}</span>}
              {o.pop && <span>POP {o.pop}</span>}
            </div>
            <div className="text-muted mt-1">
              Agend.: {formatarData(o.data_agendamento, o.hora_agendamento)}
            </div>
            {o.data_finalizacao && (
              <div className="text-success/80 mt-0.5">
                Encerr.: {formatarData(o.data_finalizacao, o.hora_finalizacao)}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================
// Filtros
// ============================================================

function FiltrosPanel({
  filtros,
  setFiltros,
  todosTecnicos,
  todosMotivos,
  onClear,
}: {
  filtros: Filtros;
  setFiltros: (f: Filtros) => void;
  todosTecnicos: string[];
  todosMotivos: string[];
  onClear: () => void;
}) {
  function toggleStatus(v: number): void {
    const s = filtros.status.includes(v)
      ? filtros.status.filter((x) => x !== v)
      : [...filtros.status, v];
    setFiltros({ ...filtros, status: s });
  }

  return (
    <div className="mb-4 rounded-2xl border border-line bg-surface p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-fg-2 block mb-1">
            Data início
          </label>
          <input
            type="date"
            value={filtros.dataInicio}
            onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })}
            className="w-full rounded-lg bg-canvas border border-line text-fg px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-fg-2 block mb-1">Data fim</label>
          <input
            type="date"
            value={filtros.dataFim}
            onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })}
            className="w-full rounded-lg bg-canvas border border-line text-fg px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-fg-2 block mb-2">
          Status visíveis
        </label>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((s) => {
            const active = filtros.status.includes(s.value);
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => toggleStatus(s.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  active
                    ? 'bg-primary-solid border-primary text-white'
                    : 'bg-canvas border-line text-fg-2 hover:bg-hover'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <ChipMultiSelect
        label="Técnicos"
        options={todosTecnicos}
        selected={filtros.tecnicos}
        onChange={(v) => setFiltros({ ...filtros, tecnicos: v })}
        emptyText="Vazio = todos"
      />

      <ChipMultiSelect
        label="Tipo de serviço (motivo)"
        options={todosMotivos}
        selected={filtros.motivos}
        onChange={(v) => setFiltros({ ...filtros, motivos: v })}
        emptyText="Vazio = todos"
      />

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
        <button
          type="button"
          onClick={onClear}
          className="px-3 py-1.5 text-xs text-fg-2 hover:text-fg"
        >
          Limpar filtros
        </button>
      </div>
    </div>
  );
}

function ChipMultiSelect({
  label,
  options,
  selected,
  onChange,
  emptyText,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (v: string[]) => void;
  emptyText: string;
}) {
  function toggle(opt: string): void {
    onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt]);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-fg-2">{label}</label>
        {selected.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-muted hover:text-fg-2 flex items-center gap-1"
          >
            <X size={11} />
            Limpar ({selected.length})
          </button>
        ) : (
          <span className="text-[10px] text-faint italic">{emptyText}</span>
        )}
      </div>
      {options.length === 0 ? (
        <p className="text-xs text-faint italic">Sem dados.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
          {options.map((o) => {
            const active = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => toggle(o)}
                className={`px-2.5 py-1 rounded-full text-xs border transition ${
                  active
                    ? 'bg-primary-solid border-primary text-white'
                    : 'bg-canvas border-line text-fg-2 hover:bg-hover'
                }`}
              >
                {o}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
